// Issue #656: Seeding story selection + initial profile embedding — pure math functions
// No DB or network dependencies. All functions are deterministic and testable.
//
// Selection is coverage-first with a medoid snap (Issue #759): farthest-first
// places the k ballot seats over the FULL pool so every distinct topic region
// earns a seat (Issue #682), then each seat snaps to the medoid of its Voronoi
// cell so an outlier is never the final representative (#688 anti-outlier goal
// without erasing sparse regions). A max-1-per-show cap keeps the seed list
// varied. The heavy computation runs on Cloud Run (which reuses the same
// primitives from kmeans.ts); this file is the canonical, testable reference
// implementation.
//
// Types are defined here (not in types/) so that the Supabase
// edge runtime can mount a single file. types/ re-exports for Node.js.

import {
  cosineSimilarity,
  normalize,
  coverageMedoidSelect,
  minPairwiseCosineSimilarity,
} from './kmeans.ts';

// Re-export for callers that import from seeding.ts
export { cosineSimilarity };

// --- Types ---

/** A story candidate in the eligible pool for seeding selection */
export interface SeedingPoolStory {
  id: string;
  slug: string;
  embedding: number[];
  // Show the story belongs to (stories.show_slug). When present, the per-show
  // cap (Issue #759) prevents two seeding stories from sharing a show unless a
  // seat's entire cell belongs to already-used shows.
  showSlug?: string;
}

/** Configuration for the seeding selection algorithm */
export interface SeedingSelectionConfig {
  maxN: number;           // number of clusters / stories to select (default 10)
  minN: number;           // if pool <= minN, return all stories (default 5)
  noveltyCosSimilarityCap: number; // unused by k-means, kept for backward compat
  seedSampleSize: number; // sample size for finding initial seed (default 2000)
  // Ignored since #759 (coverage_first_medoid); retained for backward compat —
  // admin still sends gateMinNeighbors through refresh-seeding-stories.
  densityGateMinNeighbors: number; // default 10 (ignored since #759)
  densityGateThreshold: number;    // default 0.5 (ignored since #759)
  // Ignored since #759 (coverage_first_medoid); retained for backward compat.
  coverageModeFraction: number;    // default 0.5 (ignored since #759)
}

/** Result of the seeding selection algorithm */
export interface SeedingSelectionResult {
  stories: Array<{ id: string; slug: string; rank: number; clusterSize: number; centroidEmbedding: number[] }>;
  poolCount: number;
  algo: string;
  minPairwiseCosineSimilarity: number | null;
  maxClusterPct: number;   // largest cluster as % of pool (0-1), key balance metric
}

/** A single seeding vote */
export interface SeedingVote {
  storySlug: string;
  vote: 'yes' | 'maybe' | 'no';
  embedding: number[];
}

/** Default weights for seeding votes */
export interface SeedingVoteWeights {
  yes: number;
  maybe: number;
  no: number;
}

/** Result of computing the seeding embedding */
export interface SeedingEmbeddingResult {
  embedding: number[] | null;
  confidence: number;
  counts: { yes: number; maybe: number; no: number };
}

// --- Constants ---

export const DEFAULT_SELECTION_CONFIG: SeedingSelectionConfig = {
  maxN: 10,
  minN: 5,
  noveltyCosSimilarityCap: 0.90,
  seedSampleSize: 2000,
  densityGateMinNeighbors: 10,
  densityGateThreshold: 0.5,
  coverageModeFraction: 0.5,
};

export const DEFAULT_VOTE_WEIGHTS: SeedingVoteWeights = {
  yes: 1.0,
  maybe: 0.35,
  no: 0.0,
};

// v2 (#763): centroid_embedding stores the seat's CELL MEAN (region anchor),
// not the medoid story's own embedding. The bump makes stale deploys fail
// closed via the worker↔edge-function algo handshake.
export const SEEDING_ALGO_NAME = 'coverage_first_medoid_v2';

// --- Seeding story selection ---

/**
 * Select seeding stories — coverage-first with medoid snap (Issue #759).
 *
 * COVERAGE decides where the k ballot seats are: farthest-first traversal
 * (Gonzalez k-center) over the FULL pool maximizes the minimum pairwise
 * distance among seats, so a small distinct topic region always earns a seat
 * (Issue #682). DENSITY decides who sits in each seat: every seat snaps to the
 * medoid of its Voronoi cell, so a fringe outlier is never the final
 * representative when a denser cell-mate exists. A max-1-per-show cap is
 * applied to the final representatives (duplicate allowed only when a seat's
 * entire cell belongs to already-used shows — a seat is never dropped).
 *
 * Genre-agnostic by design: it operates purely on embeddings, where topic
 * structure lives (genre encodes format, not topic). `rank` is the seat
 * traversal order; `clusterSize` is the FINAL representative's Voronoi share
 * of the pool; `minPairwiseCosineSimilarity` is computed over the FINAL
 * snapped representatives.
 *
 * `centroidEmbedding` is the seat's CELL MEAN — the region anchor (#763).
 * Votes contribute the anchor; the medoid story is only the displayed face,
 * so an admin swapping the face never changes what a vote means.
 */
export function selectSeedingByCoverage(
  pool: SeedingPoolStory[],
  config: SeedingSelectionConfig = DEFAULT_SELECTION_CONFIG
): SeedingSelectionResult {
  const { maxN, minN } = config;

  if (pool.length === 0) {
    return { stories: [], poolCount: 0, algo: SEEDING_ALGO_NAME, minPairwiseCosineSimilarity: null, maxClusterPct: 0 };
  }

  const embeddings = pool.map(s => s.embedding);

  // Small pool: return all stories, still ordered farthest-first for a sensible,
  // diversity-first presentation order.
  const k = pool.length <= minN ? pool.length : Math.min(maxN, pool.length);

  // Coverage-first seats + medoid snap + per-show cap (Issue #759).
  const { representatives, clusterSizes, cellMeans } = coverageMedoidSelect(
    embeddings,
    k,
    pool.map(s => s.showSlug),
  );

  const selectedEmbeddings = representatives.map(idx => embeddings[idx]!);
  const maxClusterSize = clusterSizes.length > 0 ? Math.max(...clusterSizes) : 0;

  return {
    stories: representatives.map((poolIdx, rank) => ({
      id: pool[poolIdx]!.id,
      slug: pool[poolIdx]!.slug,
      rank: rank + 1,
      clusterSize: clusterSizes[rank]!,
      centroidEmbedding: cellMeans[rank]!,
    })),
    poolCount: pool.length,
    algo: SEEDING_ALGO_NAME,
    minPairwiseCosineSimilarity: minPairwiseCosineSimilarity(selectedEmbeddings),
    maxClusterPct: pool.length > 0 ? maxClusterSize / pool.length : 0,
  };
}

// --- Seeding embedding computation ---

/**
 * Compute an initial user profile embedding from seeding votes.
 *
 * Each vote's story embedding is weighted (yes=1.0, maybe=0.35, no=0.0),
 * summed, then L2-normalized. If all votes are "no" (total weight = 0),
 * returns null — user will fall back to chronological feed.
 *
 * Confidence = min(1.0, (yes + 0.5 * maybe) / 6.0) — not stored in DB,
 * returned to client for UI display only.
 */
export function computeSeedingEmbedding(
  votes: SeedingVote[],
  weights: SeedingVoteWeights = DEFAULT_VOTE_WEIGHTS
): SeedingEmbeddingResult {
  const counts = { yes: 0, maybe: 0, no: 0 };

  if (votes.length === 0) {
    return { embedding: null, confidence: 0, counts };
  }

  const dim = votes[0].embedding.length;
  const sumVec = new Array(dim).fill(0);

  for (const v of votes) {
    counts[v.vote] += 1;
    const w = weights[v.vote];
    if (w > 0) {
      for (let i = 0; i < dim; i++) {
        sumVec[i] += w * v.embedding[i];
      }
    }
  }

  // Check if the result has meaningful magnitude
  const magnitude = Math.sqrt(sumVec.reduce((sum, x) => sum + x * x, 0));
  if (magnitude < 1e-10) {
    return { embedding: null, confidence: 0, counts };
  }

  const normalizedEmbedding = normalize(sumVec);
  const confidence = Math.min(1.0, (counts.yes + 0.5 * counts.maybe) / 6.0);

  return { embedding: normalizedEmbedding, confidence, counts };
}
